import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { RateCardConfig } from '../types';

const RATE_CARD_DOC_PATH = 'config/rates';

/**
 * Get current rate card configuration
 */
export const getRateCard = async (): Promise<RateCardConfig> => {
  const docRef = doc(db, RATE_CARD_DOC_PATH);
  const snapshot = await getDoc(docRef);
  
  if (snapshot.exists()) {
    return snapshot.data() as RateCardConfig;
  }
  
  // Return defaults if not exists
  return getDefaultRateCard();
};

/**
 * Default rate card configuration
 */
export const getDefaultRateCard = (): RateCardConfig => ({
  // Platform Fee Slabs
  platformFeeLow: 3,
  platformFeeMid: 5,
  platformFeeHigh: 10,
  platformFeeThresholdLow: 50,
  platformFeeThresholdHigh: 200,
  
  // Delivery
  deliveryThreshold: 100,
  deliveryFeeBelowThreshold: 40,
  deliveryFeeAboveThreshold: 20,
  maxDeliveryDistanceKm: 15,
  
  // Global Settings
  customerPlatformFeeShare: 0.4,
  gstPercentage: 5,
  chefCommissionPercentage: 10,
  
  // Payment (Optional fields)
  upiId: '',
  merchantName: '',
  upiEnabled: false,
  platformFee: 0, // Deprecated, use slabs instead
});

/**
 * Update rate card configuration
 */
export const updateRateCard = async (config: RateCardConfig): Promise<void> => {
  const docRef = doc(db, RATE_CARD_DOC_PATH);
  await setDoc(docRef, config, { merge: true });
};

/**
 * Subscribe to rate card changes in real-time
 */
export const subscribeToRateCard = (
  callback: (config: RateCardConfig) => void
): (() => void) => {
  const docRef = doc(db, RATE_CARD_DOC_PATH);
  
  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data() as RateCardConfig);
    } else {
      callback(getDefaultRateCard());
    }
  });
};

/**
 * Calculate platform fee based on order subtotal (slab-based)
 */
export const calculatePlatformFee = (
  subtotal: number,
  config: RateCardConfig
): number => {
  if (subtotal < config.platformFeeThresholdLow) {
    return config.platformFeeLow;
  }
  if (subtotal < config.platformFeeThresholdHigh) {
    return config.platformFeeMid;
  }
  return config.platformFeeHigh;
};

/**
 * Standard (non-community) delivery fee below the free-delivery threshold.
 * Prefer the Rate Card "External Base Fee", then legacy base/below fields.
 */
export const resolveStandardDeliveryFee = (config: RateCardConfig): number => {
  const candidates = [
    config.externalDeliveryBaseFee,
    config.deliveryFeeBase,
    config.deliveryFeeBelowThreshold,
  ];
  for (const value of candidates) {
    if (typeof value === "number" && !Number.isNaN(value) && value > 0) {
      return value;
    }
  }
  // Explicit 0 means free; only fall back when nothing is set
  for (const value of candidates) {
    if (typeof value === "number" && !Number.isNaN(value)) {
      return value;
    }
  }
  return 40;
};

/**
 * Calculate delivery fee based on order subtotal (standard / external).
 */
export const calculateDeliveryFee = (
  subtotal: number,
  config: RateCardConfig
): number => {
  const threshold = config.deliveryThreshold ?? 0;
  if (threshold > 0 && subtotal >= threshold) {
    return config.deliveryFeeAboveThreshold ?? 0;
  }
  return resolveStandardDeliveryFee(config);
};

/**
 * Customer-facing platform fee = round(slab * customerPlatformFeeShare).
 * Rate Card labels share as "Paid by customer" (e.g. 0.4 => 40% of slab).
 */
export const calculateCustomerPlatformFee = (
  subtotal: number,
  config: RateCardConfig
): number => {
  const slab = calculatePlatformFee(subtotal, config);
  const share =
    typeof config.customerPlatformFeeShare === "number"
      ? config.customerPlatformFeeShare
      : 1;
  return Math.round(slab * share);
};
